import { ReactElement, CSSProperties} from "react";
import {Warehouse, } from "./api/warehouse"
import {ItemCode, WarehouseCode} from "./api/types"
import {ProjectedStock } from "./projectedstock"
import {ISODate} from "./date"
import { Link } from "react-router-dom";
import { useDivision } from "./shared"

type Props = {
  countByItemCode: ReadonlyMap<ItemCode, number>,
  projectedStocks: readonly ProjectedStock[],
  warehouses: readonly Warehouse[],
  isSummary: boolean
}

function mapEq<K, V>(m1: ReadonlyMap<K, V>, m2: ReadonlyMap<K, V>): boolean {
  /**
   * Returns if two maps are equal
   * Equal means: same keys, and every key same value
   * Insert order is not taken into account
   */
  if (m1.size !== m2.size) {
    return false;
  }
  if ([...m1.keys()].some(key => !m2.has(key))) {
    return false
  }
  return [...m1.keys()].every(key => m1.get(key) === m2.get(key))
}


function mapCombine<K, V>(maps: ReadonlyArray<ReadonlyMap<K, V>>): Map<K,V> {
  /*
   * returns a new Map, which is the combination of m1 and m2
   */
  return new Map(maps.flatMap(m => [...m.entries()]))
}

function mapApply<K, V>(
  maps: ReadonlyArray<ReadonlyMap<K, V>>,
  func: (items: ReadonlyArray<V>) => V
): Map<K, V>{
  const newMap = new Map<K, V>()
  const allKeys = new Set(maps.flatMap(m => [...m.keys()]))
  for (const key of allKeys) {
    newMap.set(key, func(maps.filter(m => m.has(key)).map(m => m.get(key)!)))
  }
  return newMap;
}

function mapSum<K> (
  maps: ReadonlyArray<ReadonlyMap<K, number>>,
): Map<K, number> {
  return mapApply(
    maps, (terms: readonly number[]) => terms.reduce((a, b) => a + b, 0))
}

function mapMax<K> (
  maps: ReadonlyArray<ReadonlyMap<K, number>>,
): Map<K, number> {
  return mapApply(
    maps, (terms: readonly number[]) => Math.max(...terms))
}

function mapMin<K> (
  maps: ReadonlyArray<ReadonlyMap<K, number>>,
): Map<K, number> {
  return mapApply(
    maps, (terms: readonly number[]) => Math.min(...terms))
}

// date on which available stock is recorded; should be smaller than
// any date in the system
const AVAILABLE_STOCK_DATE = new ISODate(2000, 1, 1)

export function ScenariosTableRowDetail(
    {countByItemCode, projectedStocks, warehouses, isSummary}: Props): ReactElement {
/**
 * This component returns (warehouses.length + 1) <td> elements; the first one is
 * a list of dates, additional ones show per warehouse, one line per date,
 * the number of items that can be delivered (out of the total), using this
 * warehouse and all previous ones.
 * Warehouses that have 0 stock and never gain any, should not have anything printed
 * Lines where warehouses have negative PAS should count as 0 stock, but have
 * class="error" attached,
 * Lines should have a style="--fulfilpercentage=XX%" to indicate the fulfilment pct
 * Lines should only be printed if they change count both from line above and from
 * line to the left. If there is a line above or to the left with >=100% fulfilment,
 * the line should not be printed.
 * If there is a single itemCode, the <td>s are linked to the PAS detailed page.
 * Also there it shows how many items are available in total when the fulfilment >
 * 100%.
 * If there are multiple itemCodes, it does neither of these.
 */
  console.assert(countByItemCode.size > 0)
  console.assert(isSummary || countByItemCode.size === 1)
  const itemCodes = [...countByItemCode.keys()]
  const division = useDivision()

  type Line = {
    countByItemCode: Map<ItemCode, number>,
    totalCountByItemCode: Map<ItemCode, number>,
  }
  type Lines = Map<ISODate, Line>
  const linesByWarehouseCode = new Map<WarehouseCode, Lines | null>()

  function getPreviousLines(): Lines | undefined {
    /**
     * Gets the previous non-null lines (before the current warehouse)
     * If there is no previous non-null lines, returns undefined
     */
    return [...linesByWarehouseCode.values()]
    .reverse()
    .slice(1) // bottom (now top) one is current warehouse
    .find(l => l !== null && l.size > 0)!
  }

  const minStock = new Map(itemCodes.map(ic => [ic, 0]))

  function doesFulfillLine(m: Map<ItemCode, number>): boolean {
    return itemCodes.every(
      itemCode => m.get(itemCode)! >= countByItemCode.get(itemCode)!)
  }

  warehouses.forEach(wh => {
    const psForWh = projectedStocks.filter(ps => ps.warehouseCode === wh.code)
    if (psForWh.length === 0) {
      linesByWarehouseCode.set(wh.code, null)
      return
    }
    const lines = new Map<ISODate, Line>()
    linesByWarehouseCode.set(wh.code, lines)
    let stock = new Map(itemCodes.map(ic => [ic, 0]))

    psForWh.forEach(ps => {
      const itemCode = ps.itemCode
      stock.set(itemCode, ps.currentAvailableStock)
    })
    const stockLines = new Map<ISODate, Map<ItemCode, number>>()
    stockLines.set(AVAILABLE_STOCK_DATE, new Map(stock))

    Array.from(new Set(psForWh.flatMap(ps => [...ps.projection.keys()])))
    .sort(ISODate.compare).forEach(date => {
      const newStock = mapCombine([
        stock,
        new Map(psForWh.filter(ps => ps.projection.has(date)).map(
          ps => [ps.itemCode, ps.projection.get(date)!.eodProjectedAvailableStock]))])
      if (!mapEq(newStock, stock)) {
        stock = newStock;
        stockLines.set(date, new Map(stock))
      }
    })

    const previousLines = getPreviousLines()
    const allDates = [...new Set<ISODate>([
      ...[...(previousLines ?? new Map()).keys()],
      ...[...stockLines.keys()]
    ])].sort(ISODate.compare)

    for (const date of allDates) {
      const previousWHOnDate = previousLines === undefined ? undefined :
        [...previousLines].reverse().find(
          ([d, ]) => d.le(date))![1].totalCountByItemCode
      if (previousWHOnDate && doesFulfillLine(previousWHOnDate)) {
        break
      }
      const stockOnDate = [...stockLines].reverse().find(
          ([d, ]) => d.le(date))![1]
      const newTotalCountByItemCode = mapSum([
            previousWHOnDate ?? new Map(),
            mapMax([stockOnDate, minStock])
      ])
      lines.set(date, {
        countByItemCode: stockOnDate,
        totalCountByItemCode: newTotalCountByItemCode})
      if (doesFulfillLine(newTotalCountByItemCode)) {
        break
      }
    }
  })

  const allDates = [...new Set<ISODate>(
    [...linesByWarehouseCode.values()].flatMap(
      lines => [...(lines ?? new Map()).keys()]))].sort(ISODate.compare)

  return <><td className="dates">
    {allDates.map(date =>
      <div key={date.toISOString()}>
        {date === AVAILABLE_STOCK_DATE ? "avail. stock" 
            : date.toDDMMYYYYString()}
      </div>
     )}
  </td>
  {warehouses.map((warehouse, warehouseIndex) => {
    const lines = linesByWarehouseCode.get(warehouse.code)
    if (!lines) {
      return <td
              data-cy-itemcode={isSummary ? "summary" : itemCodes[0]}
              data-cy-warehouse={warehouse.code}
              key={warehouse.code} className="warehousedata" />
    }
    const cardUrl = itemCodes.length === 1 ? [
      "",
      "division",
      division,
      "PASDetailed",
       warehouse.code,
       itemCodes[0],
       "0" + ([...countByItemCode.values()][0] / 2).toFixed(0),
       "0" + [...countByItemCode.values()][0].toString(),
    ].map(encodeURIComponent).join("/") : undefined
      const body = allDates.map(date => {
        if (!lines.has(date)) {
            return <div
              data-cy-date={date.toISOString()}
              key={date.toISOString()} className="empty"></div>
        }
        const line = lines.get(date)!
        const nrItemsThisWH = [...line.countByItemCode.values()].reduce(
          (sum, cur) => sum + cur, 0)
        if (nrItemsThisWH === 0 && !(
          date === AVAILABLE_STOCK_DATE && warehouseIndex === 0)) {
            return <div
              data-cy-date={date.toISOString()}
              key={date.toISOString()} className="empty"></div>
        }
        const nrItemsRequested = [...countByItemCode.values()].reduce(
          (sum, cur) => sum + cur, 0)
        const nrItems = [...mapMin([
          line.totalCountByItemCode, isSummary ? countByItemCode : new Map()])]
          .reduce((sum, [_, count]) => sum + count, 0)
        const fulfilledPercentage = (nrItems / nrItemsRequested) * 100;
        const hasError = [...line.countByItemCode.values()].some(c => c < 0)
        const style = {
          "--fulfilledPercentage":
            fulfilledPercentage.toFixed(1) + "%"} as CSSProperties
        return <div key={date.toISOString()}
                    data-cy-date={date.toISOString()}
                    className={"ffpct" + (hasError ? " error" : "")
                      + (fulfilledPercentage >= 100 ? " fulfilled" : "")}
                    style={style}>
                      {nrItems} ({fulfilledPercentage.toFixed(1)}%)
                    </div>
        })
      return <td
              data-cy-itemcode={isSummary ? "summary" : itemCodes[0]}
              data-cy-warehouse={warehouse.code}
              className="warehousedata" key={warehouse.code}>
        {cardUrl ? <Link to={cardUrl}>{body}</Link> : body}
      </td>
    }
  )}
  </>
}

